// Wiping every local copy (WEB_PLAN.md §3.1 step 4): called by auth/ on SIGNED_OUT and when the membership changes,
// and by the engine itself after not_member / suspended. Deletes every IndexedDB replica/outbox this browser holds
// (all accounts, all companies) and empties the in-memory replicas of running engines.

import { deleteDatabase, forgetKnownReplicaDbNames, knownReplicaDbNames } from './replica';

/** Something holding local company data (a running engine). */
export interface LocalDataHolder {
  /** Stops using the data, empties memory and deletes its database. */
  wipe(): Promise<void>;
}

const holders = new Set<LocalDataHolder>();
const wipeListeners = new Set<() => void>();

/** Engines register while started so a wipe can stop them before deleting their databases. */
export function registerLocalData(holder: LocalDataHolder): () => void {
  holders.add(holder);
  return () => {
    holders.delete(holder);
  };
}

/** Runs after each wipe (SyncProvider restarts a fresh engine if it is still mounted). */
export function onLocalDataWiped(listener: () => void): () => void {
  wipeListeners.add(listener);
  return () => {
    wipeListeners.delete(listener);
  };
}

async function replicaDatabaseNames(): Promise<string[]> {
  const names = new Set(knownReplicaDbNames());
  try {
    if (typeof indexedDB !== 'undefined' && typeof indexedDB.databases === 'function') {
      for (const info of await indexedDB.databases()) {
        if (info.name?.startsWith('gt:')) names.add(info.name);
      }
    }
  } catch {
    // Some browsers refuse databases() in private mode; the localStorage list covers what we opened.
  }
  return [...names];
}

export async function wipeAllLocalData(): Promise<void> {
  const running = [...holders];
  holders.clear();
  await Promise.all(running.map((h) => h.wipe().catch((error) => console.warn('wipe failed', error))));
  if (typeof indexedDB !== 'undefined') {
    const names = await replicaDatabaseNames();
    await Promise.all(names.map((name) => deleteDatabase(name)));
  }
  forgetKnownReplicaDbNames();
  for (const listener of [...wipeListeners]) {
    try {
      listener();
    } catch (error) {
      console.error('wipe listener failed', error);
    }
  }
}
